import React, { useState, useMemo, useRef, useEffect } from 'react';
import { FEATURES } from '../config/features';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircleIcon, CalendarIcon, WorkspaceIcon } from '../components/icons/Icons';
import { useProjects } from '../contexts/ProjectContext';
import { useToggleSet, useCalendarMonth } from '../hooks';
import {
  TaskStatus,
  TaskStatusLabels,
  TaskStatusColors,
  Priority,
  PriorityLabels,
  PriorityColors,
  SubElement,
  ViewMode,
  WeekGroup,
  VALID_TRANSITIONS,
} from '../types';
import {
  getUserTasks,
  getSubElementPath,
  getUserName,
  calculateActualHours,
} from '../data/mockData';
import { WEEKDAY_LABELS, TASK_STATUS_SORT_ORDER } from '../constants';
import { formatHours, getWeekKey, getWeekRange, isOverdue, formatDateShort, getEffectivePriority, isAutoEscalated } from '../utils/format';
import { AppEmptyState, AppSectionHero, AppStatCard, AppToolbar } from '../components/common/AppSurface';
import { useQuickTask } from '../hooks/useQuickTask';
import { StatusChangeModal } from './task-detail/StatusChangeModal';
import './MyTasksPage.css';

export const MyTasksPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { activeProject, updateTaskStatus } = useProjects();
  const navigate = useNavigate();
  const quickTask = useQuickTask();
  const [filterStatus, setFilterStatus] = useState<TaskStatus | 'ALL'>('ALL');
  const [filterPriority, setFilterPriority] = useState<Priority | 'ALL'>('ALL');
  const [viewMode, setViewMode] = useState<ViewMode>('list');
  const [kanbanError, setKanbanError] = useState('');
  const [statusModal, setStatusModal] = useState<{ isOpen: boolean; taskId: string; newStatus: TaskStatus | null }>({
    isOpen: false,
    taskId: '',
    newStatus: null,
  });
  const { set: expandedWeeks, toggle: toggleWeek, replace: replaceExpandedWeeks } = useToggleSet();
  const { year: calendarYear, month: calendarMonthNum, prevMonth, nextMonth, monthLabel: calendarMonthLabel } = useCalendarMonth();

  const allMyTasks = useMemo(() => {
    if (!activeProject || !currentUser) return [];
    return getUserTasks(activeProject, currentUser.id);
  }, [activeProject, currentUser]);

  const myTasks = useMemo(() => {
    let tasks = allMyTasks;
    if (filterStatus !== 'ALL') {
      tasks = tasks.filter(t => t.status === filterStatus);
    }
    if (filterPriority !== 'ALL') {
      tasks = tasks.filter(t => getEffectivePriority(t) === filterPriority);
    }
    return tasks.sort((a, b) => (TASK_STATUS_SORT_ORDER[a.status] ?? 6) - (TASK_STATUS_SORT_ORDER[b.status] ?? 6));
  }, [allMyTasks, filterStatus, filterPriority]);

  // Week/day grouping for list view
  const weekGroups = useMemo(() => {
    const groups = new Map<string, Map<string, SubElement[]>>();
    const unscheduled: SubElement[] = [];

    myTasks.forEach(task => {
      if (!task.endDate) {
        unscheduled.push(task);
        return;
      }
      const endD = new Date(task.endDate + 'T00:00:00');
      const wk = getWeekKey(endD);
      if (!groups.has(wk)) groups.set(wk, new Map());
      const dayKey = task.endDate;
      const dayMap = groups.get(wk)!;
      if (!dayMap.has(dayKey)) dayMap.set(dayKey, []);
      dayMap.get(dayKey)!.push(task);
    });

    // Sort weeks
    const sortedKeys = Array.from(groups.keys()).sort();
    const result: WeekGroup[] = sortedKeys.map(wk => {
      const range = getWeekRange(wk);
      return { weekKey: wk, label: range.label, days: groups.get(wk)! };
    });

    // Unscheduled group at the end
    if (unscheduled.length > 0) {
      const unsMap = new Map<string, SubElement[]>();
      unsMap.set('unscheduled', unscheduled);
      result.push({ weekKey: 'unscheduled', label: 'Unscheduled', days: unsMap });
    }

    return result;
  }, [myTasks]);

  // Auto-expand current week + any past week with incomplete tasks.
  // Uses a ref so it only runs once (when allMyTasks first populates),
  // allowing the user to manually collapse weeks without them re-opening.
  const autoExpandDone = useRef(false);
  useEffect(() => {
    if (autoExpandDone.current || allMyTasks.length === 0) return;
    autoExpandDone.current = true;
    const todayWeek = getWeekKey(new Date());
    const weeksToExpand = new Set<string>([todayWeek]);
    allMyTasks.forEach(task => {
      if (task.status === 'FINISHED' || !task.endDate) return;
      const taskWeek = getWeekKey(new Date(task.endDate + 'T00:00:00'));
      if (taskWeek < todayWeek) weeksToExpand.add(taskWeek);
    });
    replaceExpandedWeeks(Array.from(weeksToExpand));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allMyTasks]);

  // Calendar data
  const calendarData = useMemo(() => {
    const firstDay = new Date(calendarYear, calendarMonthNum, 1);
    const lastDay = new Date(calendarYear, calendarMonthNum + 1, 0);

    // Start from Monday of the first week
    const startDow = firstDay.getDay();
    const calStart = new Date(firstDay);
    calStart.setDate(calStart.getDate() - (startDow === 0 ? 6 : startDow - 1));

    // Build 6 rows of 7 days
    const days: Date[] = [];
    const current = new Date(calStart);
    for (let i = 0; i < 42; i++) {
      days.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }

    // Map tasks to every day in their date range (startDate → endDate)
    const tasksByDate = new Map<string, SubElement[]>();
    myTasks.forEach(task => {
      if (!task.endDate) return;
      const start = task.startDate ? new Date(task.startDate + 'T00:00:00') : new Date(task.endDate + 'T00:00:00');
      const end = new Date(task.endDate + 'T00:00:00');
      const current = new Date(start);
      while (current <= end) {
        const key = current.toISOString().split('T')[0];
        if (!tasksByDate.has(key)) tasksByDate.set(key, []);
        tasksByDate.get(key)!.push(task);
        current.setDate(current.getDate() + 1);
      }
    });

    return { days, month: calendarMonthNum, year: calendarYear, firstDay, lastDay, tasksByDate };
  }, [calendarYear, calendarMonthNum, myTasks]);

  const getTaskPath = (task: { id: string }): string => {
    if (!activeProject) return '';
    const path = getSubElementPath(activeProject, task.id);
    if (!path) return '';
    return `${path.zone.code} > ${path.section.code} > ${path.element.code}`;
  };

  const getDayLabel = (dateStr: string): string => {
    if (dateStr === 'unscheduled') return 'No due date';
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
  };

  const kanbanColumns = [
    {
      id: 'not-started',
      title: 'Not Started',
      tasks: myTasks.filter(task => task.status === 'ASSIGNED'),
      targetStatus: 'ASSIGNED' as TaskStatus,
    },
    {
      id: 'in-progress',
      title: 'In Progress',
      tasks: myTasks.filter(task => task.status === 'STARTED' || task.status === 'IN_PROGRESS'),
      targetStatus: 'IN_PROGRESS' as TaskStatus,
    },
    {
      id: 'review',
      title: 'Review',
      tasks: myTasks.filter(
        task => task.status === 'PAUSED' || task.status === 'ISSUES' || task.status === 'IN_REVIEW'
      ),
      targetStatus: 'PAUSED' as TaskStatus,
    },
    {
      id: 'completed',
      title: 'Completed',
      tasks: myTasks.filter(task => task.status === 'FINISHED'),
      targetStatus: 'FINISHED' as TaskStatus,
    },
  ];

  const handleDragStart = (e: React.DragEvent, taskId: string) => {
    e.dataTransfer.setData('taskId', taskId);
    e.dataTransfer.effectAllowed = 'move';
    
    // Add a ghost image class or styling if needed
    const target = e.currentTarget as HTMLElement;
    target.classList.add('dragging');
  };

  const handleDragEnd = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('dragging');
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    
    const target = e.currentTarget as HTMLElement;
    target.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
  };

  const handleDrop = (e: React.DragEvent, targetStatus: TaskStatus) => {
    e.preventDefault();
    const target = e.currentTarget as HTMLElement;
    target.classList.remove('drag-over');
    
    const taskId = e.dataTransfer.getData('taskId');
    if (!taskId || !currentUser) return;

    const task = myTasks.find(t => t.id === taskId);
    if (!task || task.status === targetStatus) return;

    const validNextStatuses = VALID_TRANSITIONS[task.status] || [];
    if (!validNextStatuses.includes(targetStatus)) {
      setKanbanError(`Invalid transition: ${TaskStatusLabels[task.status]} to ${TaskStatusLabels[targetStatus]}. Open the task to follow the required workflow.`);
      return;
    }

    setKanbanError('');
    if (targetStatus === 'FINISHED' || targetStatus === 'ISSUES' || targetStatus === 'PAUSED') {
      setStatusModal({ isOpen: true, taskId, newStatus: targetStatus });
      return;
    }

    updateTaskStatus(taskId, targetStatus, `Moved to ${TaskStatusLabels[targetStatus]} via Kanban`, currentUser.id);
  };

  const handleKanbanModalConfirm = async (comment: string, photoFiles?: File[]): Promise<{ success: boolean; error?: string }> => {
    if (!currentUser || !statusModal.taskId || !statusModal.newStatus) {
      return { success: false, error: 'Missing task or user.' };
    }
    const result = await updateTaskStatus(statusModal.taskId, statusModal.newStatus, comment || undefined, currentUser.id, photoFiles);
    if (result.success) {
      setStatusModal({ isOpen: false, taskId: '', newStatus: null });
    }
    return result;
  };

  const handleKanbanModalClose = () => {
    setStatusModal({ isOpen: false, taskId: '', newStatus: null });
  };

  if (!activeProject || !currentUser) {
    return (
      <div className="mytasks-container">
        <AppSectionHero
          icon={<CheckCircleIcon size={28} />}
          title="My Tasks"
          subtitle="No active project."
        />
        <AppEmptyState
          icon={<WorkspaceIcon size={28} />}
          title="No active project"
          message="Select an active project to review your assigned tasks."
        />
      </div>
    );
  }

  const renderTaskCard = (task: SubElement) => {
    const overdue = isOverdue(task.endDate, task.status);
    const actualHours = calculateActualHours(task.timeEntries);
    const openTask = () => navigate(`/my-tasks/${task.id}`);
    return (
      <div
        key={task.id}
        className={`mytask-card ${task.status === 'ISSUES' ? 'has-issue' : ''} ${overdue ? 'mytask-overdue' : ''}`}
        onClick={openTask}
        role="button"
        tabIndex={0}
        onKeyDown={e => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            openTask();
          }
        }}
      >
        <div className="mytask-card-header">
          <div className="mytask-card-left">
            <span className="ws-code">{task.code}</span>
            <span className="mytask-card-name">{task.name}</span>
          </div>
          <span className="badge" style={{ backgroundColor: TaskStatusColors[task.status], color: '#fff' }}>
            {TaskStatusLabels[task.status]}
          </span>
        </div>
        <div className="mytask-card-meta">
          <span className="mytask-path">{getTaskPath(task)}</span>
          <span className="mytask-meta-sep">·</span>
          <span className="ws-priority-dot" style={{ backgroundColor: PriorityColors[getEffectivePriority(task)] }} />
          <span title={isAutoEscalated(task) ? `Auto-escalated: ${task.endDate === new Date().toISOString().split('T')[0] ? 'deadline is today' : '1 day to deadline'}` : undefined}>
            {PriorityLabels[getEffectivePriority(task)]}
            {isAutoEscalated(task) && <span className="priority-auto-badge">↑</span>}
          </span>
          {FEATURES.TIME_TRACKING && <>
            <span className="mytask-meta-sep">·</span>
            <span>{formatHours(task.estimatedHours)} est.</span>
            <span className="mytask-meta-sep">·</span>
            <span>{formatHours(actualHours)} actual</span>
          </>}
          {task.endDate && (
            <>
              <span className="mytask-meta-sep">·</span>
              <span className={overdue ? 'mytask-date-overdue' : 'mytask-date'}>
                Due {formatDateShort(task.endDate)}
              </span>
            </>
          )}
        </div>
        {task.assignedTo.length > 1 && (
          <div className="mytask-card-coworkers">
            <span className="mytask-coworker-label">With:</span>
            {task.assignedTo
              .filter(id => id !== currentUser.id)
              .map(id => <span key={id} className="mytask-coworker">{getUserName(id)}</span>)}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mytasks-container">
      <StatusChangeModal
        isOpen={statusModal.isOpen}
        newStatus={statusModal.newStatus}
        currentStatus={myTasks.find(t => t.id === statusModal.taskId)?.status ?? 'ASSIGNED'}
        onConfirm={handleKanbanModalConfirm}
        onClose={handleKanbanModalClose}
      />

      <AppSectionHero
        icon={<CheckCircleIcon size={28} />}
        title="My Tasks"
        subtitle={<>{activeProject.name} {'\u2014'} <strong>{activeProject.code}</strong></>}
        aside={
          <>
            {quickTask.canCreate && (
              <button className="app-surface-btn" onClick={quickTask.open} title="New task (c)">+ New Task</button>
            )}
            <AppStatCard icon={<CheckCircleIcon size={20} />} label="Total" value={allMyTasks.length} caption="Assigned to you" tone="blue" />
            <AppStatCard icon={<WorkspaceIcon size={20} />} label="Issues" value={allMyTasks.filter(t => t.status === 'ISSUES').length} caption="Need attention" tone="mint" />
          </>
        }
      />
      {quickTask.modal}

      {/* Quick Stats */}
      <div className="mytasks-stats">
        <AppStatCard icon={<CheckCircleIcon size={20} />} label="Done" value={allMyTasks.filter(t => t.status === 'FINISHED').length} caption="Completed tasks" tone="mint" />
        <AppStatCard icon={<CalendarIcon size={20} />} label="Active" value={allMyTasks.filter(t => t.status === 'IN_PROGRESS' || t.status === 'STARTED').length} caption="Currently moving" tone="blue" />
        <AppStatCard icon={<WorkspaceIcon size={20} />} label="Issues" value={allMyTasks.filter(t => t.status === 'ISSUES').length} caption="Blocked or flagged" />
      </div>

      {/* View Toggle + Filter */}
      <AppToolbar
        className="mytasks-toolbar"
        left={
          <div className="app-surface-chip-group mytasks-view-toggle">
            <button
              className={`app-surface-chip-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              List
            </button>
            <button
              className={`app-surface-chip-btn ${viewMode === 'calendar' ? 'active' : ''}`}
              onClick={() => setViewMode('calendar')}
            >
              <CalendarIcon size={14} /> Calendar
            </button>
            <button
              className={`app-surface-chip-btn ${viewMode === 'kanban' ? 'active' : ''}`}
              onClick={() => setViewMode('kanban')}
            >
              <WorkspaceIcon size={14} /> Kanban
            </button>
          </div>
        }
        right={
          <>
            <select
              className="app-surface-select"
              value={filterStatus}
              onChange={e => setFilterStatus(e.target.value as TaskStatus | 'ALL')}
            >
              <option value="ALL">All Statuses</option>
              {(Object.keys(TaskStatusLabels) as TaskStatus[]).map(s => (
                <option key={s} value={s}>{TaskStatusLabels[s]}</option>
              ))}
            </select>
            <select
              className="app-surface-select"
              value={filterPriority}
              onChange={e => setFilterPriority(e.target.value as Priority | 'ALL')}
            >
              <option value="ALL">All Priorities</option>
              {(Object.keys(PriorityLabels) as Priority[]).map(p => (
                <option key={p} value={p}>{PriorityLabels[p]}</option>
              ))}
            </select>
          </>
        }
      />

      {kanbanError && (
        <div className="mytasks-workflow-error" role="alert">
          {kanbanError}
        </div>
      )}

      {/* List View — Week/Day Grouping */}
      {viewMode === 'list' && (
        <div className="mytasks-list">
          {weekGroups.map(wg => {
            const isExpanded = expandedWeeks.has(wg.weekKey);
            const taskCount = Array.from(wg.days.values()).reduce((s, arr) => s + arr.length, 0);
            const todayWeek = getWeekKey(new Date());
            const isCurrent = wg.weekKey === todayWeek;

            return (
              <div key={wg.weekKey} className="mytasks-week-group">
                <button
                  className={`mytasks-week-header ${isCurrent ? 'current' : ''}`}
                  onClick={() => toggleWeek(wg.weekKey)}
                >
                  <span className="mytasks-week-chevron">{isExpanded ? '▾' : '▸'}</span>
                  <span className="mytasks-week-label">{wg.label}</span>
                  {isCurrent && <span className="mytasks-week-current-badge">This Week</span>}
                  <span className="mytasks-week-count">{taskCount} task{taskCount !== 1 ? 's' : ''}</span>
                </button>

                {isExpanded && (
                  <div className="mytasks-week-body">
                    {Array.from(wg.days.entries())
                      .sort(([a], [b]) => a.localeCompare(b))
                      .map(([dayKey, tasks]) => (
                        <div key={dayKey} className="mytasks-day-group">
                          <div className="mytasks-day-header">{getDayLabel(dayKey)}</div>
                          {tasks.map(task => renderTaskCard(task))}
                        </div>
                      ))}
                  </div>
                )}
              </div>
            );
          })}

          {myTasks.length === 0 && (
            <AppEmptyState
              className="mytasks-empty"
              icon={<CheckCircleIcon size={28} strokeWidth={1.5} />}
              title={allMyTasks.length === 0 ? 'No assigned tasks' : 'No matching tasks'}
              message={allMyTasks.length === 0
                ? 'You do not have tasks assigned in this project.'
                : 'Change the status or priority filter to see more tasks.'}
            />
          )}
        </div>
      )}

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <div className="mytasks-calendar-wrapper">
          <div className="mytasks-calendar-nav">
            <button className="mytasks-cal-nav-btn" onClick={prevMonth}>‹</button>
            <span className="mytasks-cal-month-label">
              {calendarMonthLabel}
            </span>
            <button className="mytasks-cal-nav-btn" onClick={nextMonth}>›</button>
          </div>
          <div className="mytasks-calendar-grid">
            {/* Day headers */}
            {WEEKDAY_LABELS.map(d => (
              <div key={d} className="mytasks-calendar-header-cell">{d}</div>
            ))}
            {/* Day cells */}
            {calendarData.days.map((day, i) => {
              const dayStr = day.toISOString().split('T')[0];
              const isCurrentMonth = day.getMonth() === calendarData.month;
              const isToday = dayStr === new Date().toISOString().split('T')[0];
              const dayTasks = calendarData.tasksByDate.get(dayStr) || [];

              return (
                <div
                  key={i}
                  className={`mytasks-calendar-day ${!isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today' : ''}`}
                >
                  <span className={`mytasks-cal-day-num ${isToday ? 'today' : ''}`}>
                    {day.getDate()}
                  </span>
                  <div className="mytasks-cal-day-tasks">
                    {dayTasks.slice(0, 3).map(task => (
                      <div
                        key={task.id}
                        className="mytasks-cal-task-chip"
                        style={{ backgroundColor: TaskStatusColors[task.status] }}
                        onClick={() => navigate(`/my-tasks/${task.id}`)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(`/my-tasks/${task.id}`);
                          }
                        }}
                        title={`${task.code} - ${task.name}`}
                      >
                        {task.code}
                      </div>
                    ))}
                    {dayTasks.length > 3 && (
                      <span className="mytasks-cal-more">+{dayTasks.length - 3}</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Kanban View */}
      {viewMode === 'kanban' && (
        myTasks.length === 0 ? (
          <AppEmptyState
            className="mytasks-empty"
            icon={<CheckCircleIcon size={28} strokeWidth={1.5} />}
            title={allMyTasks.length === 0 ? 'No assigned tasks' : 'No matching tasks'}
            message={allMyTasks.length === 0
              ? 'You do not have tasks assigned in this project.'
              : 'Change the status or priority filter to see more tasks.'}
          />
        ) : (
          <div className="mytasks-kanban-board">
          {kanbanColumns.map(column => (
            <section
              key={column.id}
              className={`mytasks-kanban-column ${column.id}`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, column.targetStatus)}
            >
              <div className="mytasks-kanban-column-header">
                <h2>{column.title}</h2>
                <span>{column.tasks.length}</span>
              </div>
              <div className="mytasks-kanban-list">
                {column.tasks.map(task => (
                  <article
                    key={task.id}
                    className="mytasks-kanban-card"
                    draggable
                    onDragStart={(e) => handleDragStart(e, task.id)}
                    onDragEnd={handleDragEnd}
                    onClick={() => navigate(`/my-tasks/${task.id}`)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        navigate(`/my-tasks/${task.id}`);
                      }
                    }}
                  >
                    <div className="mytasks-kanban-card-top">
                      <span className="ws-code">{task.code}</span>
                      <span className="badge small" style={{ backgroundColor: TaskStatusColors[task.status], color: '#fff' }}>
                        {TaskStatusLabels[task.status]}
                      </span>
                    </div>
                    <h3>{task.name}</h3>
                    <p>{getTaskPath(task)}</p>
                    <div className="mytasks-kanban-meta">
                      <span
                        className="ws-priority-dot"
                        style={{ backgroundColor: PriorityColors[getEffectivePriority(task)] }}
                      />
                      <span>{PriorityLabels[getEffectivePriority(task)]}</span>
                      {FEATURES.TIME_TRACKING && <span>{formatHours(task.estimatedHours)} est.</span>}
                    </div>
                    {task.endDate && (
                      <div className={`mytasks-kanban-due ${isOverdue(task.endDate, task.status) ? 'overdue' : ''}`}>
                        Due {formatDateShort(task.endDate)}
                      </div>
                    )}
                  </article>
                ))}
                {column.tasks.length === 0 && (
                  <div className="mytasks-kanban-empty">No tasks</div>
                )}
              </div>
            </section>
          ))}
          </div>
        )
      )}
    </div>
  );
};
